import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import { PageHeader } from '@/shared/components/PageHeader';
import { Spinner } from '@/shared/components/Spinner';
import { EmptyState } from '@/shared/components/EmptyState';
import { fetchAuthBlob, ApiError } from '@/lib/api-client';
import { useMyMaterials } from '../hooks/useMyMaterials';
import { MaterialCard } from '../components/MaterialCard';
import { PdfViewerModal } from '../components/PdfViewerModal';
import { MissedClassesPanel } from '../components/MissedClassesPanel';
function sortFolders(a, b) {
    if (a.order !== b.order)
        return a.order - b.order;
    return a.name.localeCompare(b.name, 'es', { numeric: true });
}
export function StudentMaterialsPage() {
    const { data, isLoading, isError } = useMyMaterials();
    const folders = useMemo(() => data?.folders ?? [], [data]);
    const materials = useMemo(() => data?.materials ?? [], [data]);
    const [currentFolderId, setCurrentFolderId] = useState(null);
    const [selectedId, setSelectedId] = useState(null);
    const [selectedTitle, setSelectedTitle] = useState('');
    const [downloadingId, setDownloadingId] = useState(null);
    const foldersById = useMemo(() => {
        const m = new Map();
        for (const f of folders)
            m.set(f.id, f);
        return m;
    }, [folders]);
    // Root = folders whose parent is null or not in the permitted set (so the
    // topmost permitted folders act as entry points).
    const visibleFolders = useMemo(() => folders
        .filter((f) => (currentFolderId === null
        ? f.parent_id === null || !foldersById.has(f.parent_id)
        : f.parent_id === currentFolderId))
        .sort(sortFolders), [folders, foldersById, currentFolderId]);
    const visibleMaterials = useMemo(() => currentFolderId === null
        ? []
        : materials
            .filter((m) => m.folder_id === currentFolderId)
            .sort((a, b) => a.name.localeCompare(b.name, 'es', { numeric: true })), [materials, currentFolderId]);
    // Breadcrumb: walk up from the current folder to the root.
    const breadcrumb = useMemo(() => {
        const chain = [];
        let id = currentFolderId;
        const seen = new Set();
        while (id && !seen.has(id)) {
            seen.add(id);
            const f = foldersById.get(id);
            if (!f)
                break;
            chain.unshift(f);
            id = f.parent_id;
        }
        return chain;
    }, [currentFolderId, foldersById]);
    function handleView(id, title) {
        setSelectedId(id);
        setSelectedTitle(title);
    }
    function handleClose() {
        setSelectedId(null);
        setSelectedTitle('');
    }
    async function handleDownload(material) {
        setDownloadingId(material.id);
        try {
            const { blob, fileName } = await fetchAuthBlob(`/materials/${material.id}/secure-download`);
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName ?? `${material.name}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        }
        catch (err) {
            alert(err instanceof ApiError ? err.message : 'No se pudo descargar el material.');
        }
        finally {
            setDownloadingId(null);
        }
    }
    if (isLoading) {
        return (_jsx("div", { className: "flex h-64 items-center justify-center", children: _jsx(Spinner, { size: "lg" }) }));
    }
    if (isError) {
        return (_jsx("p", { role: "alert", className: "text-sm text-red-600 dark:text-red-400", children: "Error al cargar los materiales. Recarga la p\u00E1gina." }));
    }
    const isEmpty = folders.length === 0 && materials.length === 0;
    return (_jsxs("div", { children: [_jsx(PageHeader, { title: "Mis materiales" }), _jsx(MissedClassesPanel, {}), isEmpty ? (_jsx(EmptyState, { icon: "folder", title: "No tienes materiales asignados", description: "Cuando el director o un profesor te asigne materiales, aparecer\u00E1n aqu\u00ED." })) : (_jsxs(_Fragment, { children: [_jsxs("nav", { className: "mb-4 flex flex-wrap items-center gap-1 text-sm", "aria-label": "Ruta de carpetas", children: [_jsx("button", { onClick: () => setCurrentFolderId(null), className: "font-medium text-brand hover:underline dark:text-brand-light", children: "Inicio" }), breadcrumb.map((f) => (_jsxs("span", { className: "flex items-center gap-1", children: [_jsx("span", { className: "text-gray-400 dark:text-gray-600", children: "/" }), _jsx("button", { onClick: () => setCurrentFolderId(f.id), className: "font-medium text-brand hover:underline dark:text-brand-light", children: f.name })] }, f.id)))] }), visibleFolders.length === 0 && visibleMaterials.length === 0 && (_jsx("p", { className: "text-sm text-gray-500 dark:text-gray-400", children: "Esta carpeta est\u00E1 vac\u00EDa." })), visibleFolders.length > 0 && (_jsx("div", { className: "mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3", children: visibleFolders.map((f) => (_jsxs("button", { onClick: () => setCurrentFolderId(f.id), className: "flex items-center gap-3 rounded-xl border border-gray-200 bg-white p-4 text-left transition-colors hover:border-brand hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:hover:bg-gray-800/50", children: [_jsx("div", { className: "flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10", children: _jsx("svg", { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.5, className: "h-6 w-6 text-brand dark:text-brand-light", children: _jsx("path", { d: "M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" }) }) }), _jsx("span", { className: "font-medium text-gray-900 dark:text-gray-100 line-clamp-2", children: f.name })] }, f.id))) })), visibleMaterials.length > 0 && (_jsx("div", { className: "grid gap-4 sm:grid-cols-2 lg:grid-cols-3", children: visibleMaterials.map((m) => (_jsx(MaterialCard, { material: m, onView: handleView, onDownload: handleDownload, downloading: downloadingId === m.id }, m.id))) }))] })), _jsx(PdfViewerModal, { materialId: selectedId, title: selectedTitle, onClose: handleClose })] }));
}
//# sourceMappingURL=StudentMaterialsPage.js.map